import React, { useEffect, useRef, useState } from 'react';
import { FlatList, ListRenderItem, TouchableOpacity, View, ViewToken } from 'react-native';
import Card from './Card';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';
import Divider from './Divider';
import Badge from './Badge';
import { Hoverable, ScrollView } from 'react-native-web-hover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, IconDefinition } from '@fortawesome/free-regular-svg-icons';
import moment from 'moment';
import { translate } from '../services/translate';
import Tooltip from './Tooltip';
import { faClock } from '@fortawesome/pro-regular-svg-icons';

type DurationStatus = 'onTime' | 'onGoing' | 'warning' | 'critical';

interface KanbanCardParams {
    id: number;
    start: moment.MomentInput;
    finish?: moment.MomentInput;
    title: string;
    subTitle?: { title: string; content: string; };
    responsible?: { id: number; warName: string | null; name: string; };
    tags?: { id: number; name: string; color: string; }[];
    duration?: {
        start: moment.MomentInput;
        finish?: moment.MomentInput;
        status?: DurationStatus;
    };
    additionalInfo?: string[];
    priority?: 'low' | 'normal' | 'medium' | 'high' | 'critical';
    counter?: number;
    image?: React.ReactNode;
}

type KanbanParams<ItemT> = {
    cards: { content: ItemT; column: string; key: string; }[];
    columns: { key: string; label: string; }[];
    onClickCard: (item: { content: ItemT; column: string; key: string; }) => void;
    onViewableItemsChanged?: (info: { viewableItems: Array<ViewToken>; changed: Array<ViewToken>; }) => void;
} & ({
    customRenderCardItem?: undefined;
    cardContent: (item: { content: ItemT; column: string; key: string; }) => KanbanCardParams;
} | {
    customRenderCardItem: ListRenderItem<{ content: ItemT; column: string; key: string; }>;
    cardContent?: undefined;
});

const pagePadding = 16;
const scrollViewPadding = 24;
const columnHeaderSize = 80;

function KanbanResponsible(params: { responsible?: { id: number; warName: string | null; name: string; }; }) {
    const { styles, theme } = useStyles(stylesheet);

    const colorLength = theme.colors.responsible.length;

    return <View style={styles.cardLineWithGap}>
        <Tooltip disabled={!params.responsible} tooltipContent={params.responsible ? params.responsible.warName || params.responsible.name : ''} tooltipDirection='right'>
            <View style={styles.userIcon(params.responsible && theme.colors.responsible[params.responsible.id % colorLength])}>
                {!params.responsible
                    ? <FontAwesomeIcon icon={faUser} fontSize={9} color={theme.colors.neutral[500]} />
                    : <MyAppText style={{
                        color: theme.colors.card.background,
                        fontSize: 12,
                        fontWeight: 'bold',
                    }}>{params.responsible.name[0].toUpperCase()}</MyAppText>
                }
            </View>
        </Tooltip>
        {!params.responsible &&
            <MyAppText style={styles.cardHeaderText}>
                {translate('maleNone')}
            </MyAppText>
        }
    </View>;
}


function getDuration(paramStart: moment.MomentInput, paramFinish?: moment.MomentInput) {
    const finish = paramFinish ? moment(paramFinish) : moment();
    const start = moment(paramStart);

    const totalSeconds = Math.max(0, finish.diff(start, 'seconds'));

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const pad = (n: number) => String(n).padStart(2, '0');

    return `${pad(hours)}h${pad(minutes)}min`;

}

function KanbanCard(params: KanbanCardParams) {
    const { styles, theme } = useStyles(stylesheet);
    const [duration, setDuration] = useState('');

    useEffect(() => {
        if (!params.duration) {
            return;
        }
        setDuration(getDuration(params.duration.start, params.duration.finish));
    }, [params.duration?.start, params.duration?.finish]);

    return <View style={{ gap: 16 }}>
        {params.counter &&
            <MyAppText style={{
                position: 'absolute',
                top: -21,
                right: -20,
                backgroundColor: '#A21900',
                color: '#FFFFFF',
                borderRadius: 8,
                minWidth: 16,
                height: 16,
                paddingHorizontal: 5,
                fontSize: 10,
                fontWeight: 'bold',
                textAlign: 'center',
                alignContent: 'center',
            }}>{params.counter > 99 ? '99+' : params.counter}</MyAppText>
        }
        <View style={{ gap: 12 }}>
            <View style={styles.cardLineWithSpaceBetween}>
                <MyAppText style={styles.cardHeaderText}>
                    {'#' + params.id}
                </MyAppText>
                <MyAppText style={styles.cardHeaderText}>
                    {moment(params.start).format('DD/MM/YYYY - HH:mm:ss')}
                </MyAppText>
            </View>
            {params.tags && params.tags.length > 0 &&
                <View style={{ flexDirection: 'row', gap: 8, zIndex: 3, }}>
                    {params.tags.slice(0, 4).map((tag, index) => <Tooltip key={tag.id} tooltipContent={tag.name} tooltipDirection={index < 3 ? 'right' : 'left'} style={{ width: 160 }}>
                        <View style={{ height: 10, width: 40, backgroundColor: tag.color, borderRadius: 5 }} />
                    </Tooltip>)}
                    {params.tags.slice(4).length > 0 &&
                        <Tooltip tooltipContent={<View>{params.tags.slice(4).map(tag => <View key={tag.id}>{tag.name}</View>)}</View>} tooltipDirection={'left'} style={{ width: 160 }}>
                            <View style={{
                                height: 10,
                                width: 40,
                                backgroundColor: theme.colors.neutral[400],
                                borderRadius: 5,
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                paddingRight: 3,
                            }}>
                                <MyAppText style={{
                                    color: theme.colors.card.background,
                                    fontWeight: 'bold',
                                    fontSize: 9,
                                }}>{params.tags.slice(4).length}</MyAppText>
                            </View>
                        </Tooltip>
                    }
                </View>
            }
            <View style={{ gap: 4 }}>

                <View style={{ flexDirection: 'row', gap: 20 }}>
                    <View style={{ flex: 1 }}>
                        <MyAppText style={styles.cardTitleText}>{params.title}</MyAppText>
                        {params.subTitle &&
                            <MyAppText style={styles.cardTitleText}>
                                {params.subTitle.title}{': '}
                                <MyAppText style={styles.cardCameraNameText}>{params.subTitle.content}</MyAppText>
                            </MyAppText>
                        }


                        {(params.additionalInfo ?? []).map((info, index) => <View style={styles.cardLineWithGap} key={index}>
                            <MyAppText style={[styles.cardCameraNameText, styles.overflowEllipsis]}>
                                {info}
                            </MyAppText>
                        </View>
                        )}

                        {params.finish && <View style={{ flexDirection: 'row', gap: 4 }}>
                            <MyAppText style={styles.cardTitleText}>{translate('concludedOn')}{':'}</MyAppText>
                            <MyAppText style={styles.cardCameraNameText}>{moment(params.finish).format('DD/MM/YYYY - HH:mm:ss')}</MyAppText>
                        </View>
                        }
                    </View>
                    {params.image}
                </View>
            </View>
        </View>
        <View style={{ gap: 12 }}>
            <Divider color={theme.colors.backgrounds['BG-1']} />
            <View style={styles.cardLineWithSpaceBetween}>
                <KanbanResponsible responsible={params.responsible} />
                <View style={{ gap: 8, alignItems: 'center' }}>
                    {params.duration && <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                        <FontAwesomeIcon
                            icon={faClock as IconDefinition}
                            color={params.duration.status
                                ? theme.colors.durationStatus[params.duration.status].text
                                : theme.colors.neutral[400]}
                        />
                        <MyAppText style={styles.duration(params.duration.status)}>{duration}</MyAppText>
                    </View>}
                    {params.priority && <Badge
                        color={params.priority === 'low' ? 'blue' :
                            params.priority === 'critical' ? 'red' :
                                params.priority === 'high' ? 'orange' :
                                    params.priority === 'normal' ? 'green' :
                                        'yellow'
                        }
                        mode={'SQUARE'}
                    >{translate(params.priority)}</Badge>
                    }
                </View>
            </View>
        </View>
    </View >;
}

export default function Kanban<ItemT>(params: KanbanParams<ItemT>) {
    const { styles, theme } = useStyles(stylesheet);
    const [totalPageHeight, setTotalPageHeight] = useState(0);

    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const observer = new ResizeObserver(entries => {
            const { height } = entries[0].contentRect;
            setTotalPageHeight(height);
        });

        observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    const [columnHeights, setColumnHeights] = useState<Record<string, number>>({});

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const scrollRef = useRef<ScrollView>(null);
    const startX = useRef(0);
    const startScrollLeft = useRef(0);
    const isDragging = useRef(false);
    const [isGrabbing, setIsGrabbing] = useState(false);

    const onPointerDown = (e: React.PointerEvent) => {
        const el = scrollRef.current;

        startX.current = e.clientX;
        startScrollLeft.current = el.scrollLeft;

        isDragging.current = true;
        setIsGrabbing(true);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!isDragging.current) {
            return;
        }

        const dx = e.clientX - startX.current;
        if (Math.abs(dx) < 5) {
            return;
        }

        scrollRef.current.scrollLeft = startScrollLeft.current - dx;

    };

    const onPointerUp = () => {
        isDragging.current = false;
        setIsGrabbing(false);
    };

    const updateColumnHeight = (key: string, height: number) => {
        setColumnHeights(prev => ({
            ...prev,
            [key]: height,
        }));
    };

    const [hasScroll, setHasScroll] = useState(false);

    function checkHasScroll() {
        const node: HTMLDivElement | undefined = scrollRef.current?.getScrollableNode();

        if (!node) {
            return;
        }

        setHasScroll(node.scrollWidth > node.clientWidth);

    }

    return <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
            flex: 1,
            touchAction: 'pan-y',
            cursor: !hasScroll ? 'default' : isGrabbing ? 'grabbing' : 'grab',
        }}
        ref={ref}
    >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <ScrollView ref={scrollRef}
            horizontal={true}
            contentContainerStyle={styles.container(totalPageHeight - pagePadding)}
            onLayout={() => checkHasScroll()}
            onContentSizeChange={() => checkHasScroll()}
        >
            {params.columns.map(column => <Card key={column.key} style={styles.column(Math.min(totalPageHeight - scrollViewPadding, columnHeights[column.key] + columnHeaderSize))}>
                <View style={styles.columnHeader}>
                    <View style={styles.columnHeaderRow}>
                        <MyAppText style={styles.columnHeaderText}>{column.label}</MyAppText>
                        <Badge mode='ROUNDED' backgroundColor={styles.columnHeaderText.color} size='small' textColor={theme.colors.card.background}>
                            {params.cards.filter(card => card.column === column.key).length}
                        </Badge>
                    </View>
                    <Divider color={theme.colors.backgrounds['BG-1']} />
                </View>
                <FlatList
                    style={{ paddingHorizontal: 16, paddingBottom: 8 }}
                    data={params.cards.filter(card => card.column === column.key)}
                    onContentSizeChange={(_, h) => updateColumnHeight(column.key, h)}
                    onViewableItemsChanged={params.onViewableItemsChanged}
                    renderItem={(item) => (<TouchableOpacity onPress={() => params.onClickCard(item.item)}>
                        <Hoverable>
                            {({ hovered }) => (
                                <Card style={styles.card(hovered)}>
                                    {params.cardContent ? <KanbanCard {...params.cardContent(item.item)} /> : params.customRenderCardItem(item)}
                                </Card>
                            )}
                        </Hoverable>
                    </TouchableOpacity>)}
                    contentContainerStyle={{ gap: 12, paddingTop: 16 }}
                />
            </Card >)}
        </ScrollView>
    </div>;
}

const stylesheet = createStyleSheet(theme => ({
    container: (height: number) => ({
        flexDirection: 'row',
        flex: 1,
        height,
        gap: 12,
    }),
    columnHeader: {
        height: 28,
        paddingHorizontal: 16,
        justifyContent: 'space-between',
    },
    columnHeaderRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
    },
    columnHeaderText: {
        color: theme.colors.neutral[500],
        fontWeight: '600',
        fontSize: 14,
    },
    column: (height: number) => ({
        maxWidth: 345,
        minWidth: 345,
        height,
        padding: 0,
        paddingTop: 16,
        cursor: 'default',
    }),
    cardsContainer: {
        gap: 12,
    },
    card: (hovered: boolean) => ({
        borderColor: hovered ? theme.colors.neutral[300] : theme.colors.neutral[200],
        borderRadius: 6,
        padding: 12,
    }),
    // Kanban Card
    cardLineWithSpaceBetween: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cardHeaderText: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.neutral[400],
    },
    cardTitleText: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.secondary[400],
    },
    cardLineWithGap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    cardCameraNameText: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.neutral[400],
    },
    overflowEllipsis: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    userIcon: (backgroundColor?: string) => ({
        backgroundColor: backgroundColor ?? theme.colors.neutral[200],
        height: 20,
        width: 20,
        borderRadius: 10,
        justifyContent: 'center',
        alignItems: 'center',
    }),
    duration: (status?: DurationStatus) => ({
        fontSize: 12,
        fontWeight: '600',
        color: status ? theme.colors.durationStatus[status].text : theme.colors.neutral[400],
    }),
}));
